<!--
+------------------------------------------------------------------+
| Author: Jeffrey Thümann                                          |
| Component: StatBar                                               |
| Section: components                                              |
| Purpose: Horizontal stat bar visualizing comparative totals.     |
+------------------------------------------------------------------+
-->
<template>
    <div class="StatBar_wrapper wrapper">
        <StatCard :label="$t('home.statCards.familyMembers')" :value="totalPeople" :maxValue="maxValue" :delay="100"
            :change="monthlyChanges.totalPeople" :animateOnMount="animateOnMount" href="/stamboom?view=relaties" />
        <StatCard :label="$t('home.statCards.surnames')" :value="uniqueLastnames" :maxValue="maxValue" :delay="200"
            :change="monthlyChanges.uniqueLastnames" :animateOnMount="animateOnMount" href="/stamboom?view=achternamen" />
        <StatCard :label="$t('home.statCards.documents')" :value="totalDocuments" :maxValue="maxValue" :delay="300"
            :change="monthlyChanges.totalDocuments" :animateOnMount="animateOnMount"
            href="/fotos-en-documenten?type=documents" />
        <StatCard :label="$t('home.statCards.photos')" :value="totalPhotos" :maxValue="maxValue" :delay="400"
            :change="monthlyChanges.totalPhotos" :animateOnMount="animateOnMount"
            href="/fotos-en-documenten?type=photos" />
        <StatCard :label="$t('home.statCards.places')" :value="totalPlaces" :maxValue="maxValue" :delay="500"
            :change="monthlyChanges.totalPlaces" :animateOnMount="animateOnMount" href="/plaatsen" />
    </div>
</template>

<script>
import StatCard from '@/atoms/StatCard.vue';
import { fetchCurrentSiteStats } from '@/api/apiService';
import { fetchRuntimeJson } from '@/utils/runtimeJson';
import { buildCurrentMonthDeltas } from '@/utils/siteStatsGrowth';

export default {
    name: 'StatBar',
    components: {
        StatCard
    },
    props: {
        animateOnMount: { type: Boolean, default: true }
    },
    data() {
        return {
            totalPeople: 0,
            uniqueLastnames: 0,
            totalDocuments: 0,
            totalPhotos: 0,
            totalPlaces: 0,
            maxValue: 0,
            monthlyChanges: {}
        };
    },
    methods: {
        calculateMaxValue() {
            const values = [
                this.totalPeople,
                this.totalDocuments,
                this.totalPhotos,
                this.uniqueLastnames,
                this.totalPlaces
            ];
            this.maxValue = Math.max(...values);
        },
        async loadMonthlyChanges() {
            const growthData = await this.fetchOptionalJson('/data/getSiteStats/siteStatsGrowth.json');
            if (!growthData?.rows) return;

            try {
                const currentStats = await fetchCurrentSiteStats();
                this.monthlyChanges = buildCurrentMonthDeltas(growthData, currentStats);
            } catch (error) {
                this.monthlyChanges = {};
            }
        },
        async fetchOptionalJson(path) {
            try {
                const response = await fetch(path, { cache: 'no-store' });
                if (!response.ok) return null;
                return response.json();
            } catch (error) {
                return null;
            }
        }
    },
    async mounted() {
        const [
            totalPeopleData,
            totalDocumentsData,
            totalPhotosData,
            totalPlacesData,
            uniqueLastnamesData
        ] = await Promise.all([
            fetchRuntimeJson('/data/getSiteStats/totalPeople.json', {}),
            fetchRuntimeJson('/data/getSiteStats/totalDocuments.json', {}),
            fetchRuntimeJson('/data/getSiteStats/totalPhotos.json', {}),
            fetchRuntimeJson('/data/getSiteStats/totalPlaces.json', {}),
            fetchRuntimeJson('/data/getSiteStats/uniqueLastnames.json', {})
        ]);

        this.totalPeople = totalPeopleData.total || 0;
        this.totalDocuments = totalDocumentsData.totalDocuments || 0;
        this.totalPhotos = totalPhotosData.totalPhotos || 0;
        this.totalPlaces = totalPlacesData.totalPlaces || 0;
        this.uniqueLastnames = uniqueLastnamesData.uniqueLastnames || 0;
        this.calculateMaxValue();
        this.loadMonthlyChanges();
    }
}
</script>

<style scoped>
.StatBar_wrapper {
    display: flex;
    align-items: center;
    gap: 24px;
    padding: 0 20px;
}

@media (max-width: 860px) {
    .StarBar_container {
        background: var(--brand-bg-2);
        padding-top: 48px;
    }

    .StatBar_wrapper {
        flex-direction: row;
        flex-wrap: wrap;
        align-items: flex-start;
        gap: 16px;
    }

    .StatBar_wrapper a {
        width: calc(50% - 8px);
        flex: auto;
        align-self: stretch;
        justify-content: center;
    }
}
</style>
